import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import ScreenshotCarousel from '../components/ScreenshotCarousel';

const HowItWorksPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-saverly-primary/10 to-saverly-deep/10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-saverly-dark font-poppins mb-6">
            How Saverly
            <span className="block text-saverly-primary">Works</span>
          </h1>
          <p className="text-xl text-saverly-gray font-inter max-w-3xl mx-auto">
            From meal planning to grocery savings in just a few simple steps.
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            
            {/* Step 1 */}
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-saverly-primary text-white rounded-full flex items-center justify-center font-bold text-xl">
                    1
                  </div>
                  <h2 className="text-3xl font-bold text-saverly-dark font-poppins">Set Your Store & Preferences</h2>
                </div>
                <p className="text-lg text-saverly-gray font-inter leading-relaxed">
                  Select your local store (Kroger, Fry's, Ralph's, King Soopers, Smith's, or other Kroger family stores) and set your dietary preferences. Choose from 18 dietary options including Vegetarian, Vegan, Pescatarian, Gluten-Free, Keto, and more. Set your family size and cuisine preferences for personalized meal plans.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center text-saverly-gray">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    18 dietary preferences (Vegan, Keto, Halal, etc.)
                  </li>
                  <li className="flex items-center text-saverly-gray">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    10+ cuisine preferences
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Family size & cooking time preferences
                  </li>
                </ul>
              </div>
              <div className="flex justify-center items-center">
                <div className="w-96">
                  <ScreenshotCarousel
                    images={[
                      { src: '/screenshots/meal-plan-1.png', caption: 'Set family size and dietary preferences' }
                    ]}
                    alt="Set Your Preferences"
                  />
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="flex justify-center items-center lg:order-first">
                <div className="w-96">
                  <ScreenshotCarousel
                    images={[
                      { src: '/screenshots/meal-plan-3.png', caption: 'Browse live sales by category with prices and discounts' }
                    ]}
                    alt="Browse Sales & Pick a Product"
                  />
                </div>
              </div>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-saverly-accent text-white rounded-full flex items-center justify-center font-bold text-xl">
                    2
                  </div>
                  <h2 className="text-3xl font-bold text-saverly-dark font-poppins">Browse Sales & Pick a Product</h2>
                </div>
                <p className="text-lg text-gray-700 font-inter leading-relaxed">
                  Browse live sales from Kroger family stores by category. See real-time prices, discounts, and product details. Tap any product to view full nutrition info, stock status, and sale expiration dates.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Search or filter by category
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    View detailed product modals
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Add to shopping list or start meal plan
                  </li>
                </ul>
              </div>
            </div>

            {/* Step 3 */}
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-saverly-deep text-white rounded-full flex items-center justify-center font-bold text-xl">
                    3
                  </div>
                  <h2 className="text-3xl font-bold text-saverly-dark font-poppins">Smart Product Matching</h2>
                </div>
                <p className="text-lg text-gray-700 font-inter leading-relaxed">
                  Every ingredient is automatically matched to real products from Kroger family stores using our intelligent scoring system. Products are filtered by category, scored for relevance, and ranked by quality. Fresh items get fresh products, spices get spices—never mistakes.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Category-based filtering
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Intelligent scoring algorithm
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    500+ ingredient-to-category mappings
                  </li>
                </ul>
              </div>
              <div className="flex justify-center items-center">
                <div className="w-96">
                  <ScreenshotCarousel
                    images={[
                      { src: '/screenshots/product-matching-1.png', caption: 'Ingredients automatically matched to products' }
                    ]}
                    alt="Smart Product Matching"
                  />
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="flex justify-center items-center lg:order-first">
                <div className="w-96">
                  <ScreenshotCarousel
                    images={[
                      { src: '/screenshots/shopping-cart-1.png', caption: 'Check off items as you shop, organized by aisle' }
                    ]}
                    alt="Shop with Your Smart List"
                  />
                </div>
              </div>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-saverly-primary text-white rounded-full flex items-center justify-center font-bold text-xl">
                    4
                  </div>
                  <h2 className="text-3xl font-bold text-saverly-dark font-poppins">Shop with Your Smart List</h2>
                </div>
                <p className="text-lg text-gray-700 font-inter leading-relaxed">
                  All matched ingredients appear in your shopping list with images, prices, and aisle locations. Check items off as you shop. Export or share your list anytime.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Organized by aisle for efficient shopping
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Product images & prices included
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Multiple shopping lists supported
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 font-poppins mb-12">
            The Results Speak for Themselves
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-4xl font-bold text-saverly-primary mb-4">5 recipes</div>
              <h3 className="text-xl font-bold text-saverly-dark mb-2">Free Tier Weekly Limit</h3>
              <p className="text-saverly-gray">Try the app with 5 recipes per week, then upgrade for unlimited</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-4xl font-bold text-saverly-accent mb-4">10-20</div>
              <h3 className="text-xl font-bold text-saverly-dark mb-2">Complete Meals</h3>
              <p className="text-saverly-gray">Main dishes with sides, vegetables, and seasonings</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-4xl font-bold text-saverly-deep mb-4">500+</div>
              <h3 className="text-xl font-bold text-saverly-dark mb-2">Ingredient Mappings</h3>
              <p className="text-saverly-gray">Comprehensive database ensures perfect product matching</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-saverly-primary to-saverly-accent text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold font-poppins mb-6">
            Ready to Transform Your Meal Planning?
          </h2>
          <p className="text-xl text-white/80 font-inter mb-8">
            Join thousands of families saving time and money with Saverly.
          </p>
          <Link to="/download">
            <Button size="lg" className="bg-white text-saverly-primary hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105">
              Get Started Free
            </Button>
          </Link>
          <p className="text-sm text-white/70 mt-4">No credit card required • Free forever</p>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage; 